import { Alert } from '@shared/components';
import { useAuth } from '@/features/auth';
import type { CancellationProgressWatch } from '../../hooks/useCancellationProgressWatch';
import { useCancellationSteps } from '../../hooks/useCancellationSteps';
import { useCompleteCancellation } from '../../hooks/useCompleteCancellation';
import { cancellationErrorMessage } from '../../utils/cancellationError';
import { cancellationSectionView, isSettlementInFlight } from '../../utils/cancellationSection';
import type { Agreement } from '../../types/agreements.types';
import { CancelAgreementPanel } from './CancelAgreementPanel/CancelAgreementPanel';
import { CancellationPanel } from './CancellationPanel/CancellationPanel';
import { CancellationRefreshPrompt } from './CancellationRefreshPrompt';
import { CancellationStatusLine } from './CancellationStatusLine';
import { CancellationUnavailable } from './CancellationUnavailable';
import { CompleteCancellationPanel } from './CompleteCancellationPanel/CompleteCancellationPanel';

interface Props {
    agreement: Agreement;
    watch: CancellationProgressWatch;
}

export function CancellationsSection({ agreement, watch }: Props) {
    const { isInternal } = useAuth();
    const steps = useCancellationSteps(agreement.id);
    const completion = useCompleteCancellation(agreement.id);
    const { cancelAction, cancellation } = agreement;

    if (!cancelAction) return null;

    const view = cancellationSectionView({ status: agreement.status, cancelAction, cancellation, canComplete: isInternal });

    return (
        <>
            {steps.started && (view.kind === 'start' || view.kind === 'in_progress') && (
                <Alert variant="success" role="status">
                    Cancellation started
                </Alert>
            )}
            {view.kind === 'start' && (
                <CancelAgreementPanel
                    agreementRef={agreement.agreementRef}
                    action={cancelAction}
                    step={steps.step}
                    pending={steps.pending}
                    serverError={steps.serverError}
                    onContinue={steps.onContinue}
                    onBack={steps.onBack}
                    onSubmit={steps.onSubmit}
                />
            )}
            {view.kind === 'unavailable' && <CancellationUnavailable message={view.message} />}
            {view.kind === 'in_progress' && (
                <>
                    <CancellationStatusLine progress={view.progress} />
                    {watch.state === 'timedOut' && isSettlementInFlight(agreement) && (
                        <CancellationRefreshPrompt onRefresh={watch.refresh} />
                    )}
                    {view.completableReason && (
                        <CompleteCancellationPanel
                            reason={view.completableReason}
                            pending={completion.isPending}
                            serverError={completion.error ? cancellationErrorMessage(completion.error) : null}
                            onSubmit={(option, note) => completion.mutate({ option, note })}
                        />
                    )}
                </>
            )}
            {cancellation && <CancellationPanel cancellation={cancellation} />}
        </>
    );
}
